import h from 'hyperscript';
import $ from 'jquery';

import infoIcon from '@disy/cadenza-icons/info.svg';
import type { EventKey } from '@disy/lib/event-util';
import { ChangeEvent, on, unByKey } from '@disy/lib/event-util';
import { icon } from '@disy/lib/icon';
import { createButton } from '@disy/ui/button';

import { Select } from 'ui/select/select';
import { type SelectOption, isPlaceholderOption } from 'ui/select/select-types';
import { createToggletip } from 'ui/toggletip/toggletip';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import { getSimpleDataProtectionContextsForRepository } from 'cadenza/api-client/data-protection-context-api';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import type { SimpleDataProtectionContextDto } from 'cadenza/api-client/dto/simple-data-protection-context-dto';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import { repositoryItemIdEquals } from 'cadenza/repository/model/repository-item-id';
import {
  type ResourceSelectItem,
  templateResourceResult,
  templateResourceSelection,
  toResourceSelectOption,
} from 'cadenza/resource-filter/resource-filter';

import './data-protection-context-picker.css';
import i18n from './data-protection-context-picker.properties';

const COMPONENT_NAME = 'd-data-protection-context-picker';

interface ItemDescription {
  dpcId: DataProtectionContextId;
  description?: string;
}

export class DataProtectionContextPicker extends HTMLElement {
  #select!: Select;
  #repository: RepositoryDto;
  #dropdownPanel?: HTMLElement;
  #emptyInfo?: HTMLElement;

  #pointerEventKey?: EventKey;

  constructor(repository: RepositoryDto, dataProtectionContextId?: DataProtectionContextId) {
    super();
    this.classList.add(COMPONENT_NAME);
    this.#repository = repository;
    this.#createContent(dataProtectionContextId);
  }

  connectedCallback() {
    // if the picker is used within an inline-editing, prevent the closing of inline-editing when clicked on the create-dialog
    if (this.closest('.d-inline-editing')) {
      this.#pointerEventKey = on(window, 'pointerdown', (e) => {
        // inline-editing.ts closes itself when clicked outside of it.
        // But we want to prevent that when the click is within the 'd-create-data-protection-context-dialog'
        if (e.target && (e.target as HTMLElement).closest('.d-create-data-protection-context-dialog') != null) {
          e.stopImmediatePropagation();
        }
      });
    }
  }

  disconnectedCallback() {
    unByKey(this.#pointerEventKey);
  }

  async #createContent(dataProtectionContextId?: DataProtectionContextId) {
    const dataProtectionContexts = await this.#getDataProtectionContexts();
    this.#select = new Select({
      data:
        dataProtectionContexts.length > 0 ?
          buildAllSelectOptionGroup(dataProtectionContexts, dataProtectionContextId)
        : [],
      placeholder: ' ',
      required: true,
      dropdownSearch: true,
      styleClass: `${COMPONENT_NAME}--select`,
      dropdownStyleClass: `${COMPONENT_NAME}--select-dropdown`,
      templateResult: (item, searchText) => dataProtectionContextTemplateResult(item, searchText),
      templateSelection: (item) => templateResourceSelection(item as ResourceSelectItem),
    });
    $(this.#select).on('select2:open', () => {
      let dropdownPanel = this.#dropdownPanel;
      if (!dropdownPanel) {
        dropdownPanel = this.#buildDropdownCustomPanel(dataProtectionContexts.length !== 0);
        this.#dropdownPanel = dropdownPanel;
      }
      this.#select.searchInput!.parentElement!.after(dropdownPanel);
      // we do not use select api here, select does not know that there is something in the dropdown, so it hides it. Hence, we need to make it not hidden.
      this.#select._select2.dropdown.$dropdown.removeClass('hidden');
    });
    // Do not close the select if d-create-data-protection-context-dialog is shown
    $(this.#select).on('select2:closing', (e) => {
      if (document.body.querySelector('.d-create-data-protection-context-dialog') != null) {
        e.preventDefault();
      }
    });
    this.append(h('form-group', [h('label', i18n('dataProtectionContext.label')), this.#select]));
  }

  #getDataProtectionContexts() {
    return getSimpleDataProtectionContextsForRepository(this.#repository.name);
  }

  #buildDropdownCustomPanel(hasDataProtectionContexts: boolean) {
    this.#emptyInfo =
      hasDataProtectionContexts ? undefined : (
        h('d-alert.d-alert-small.d-alert-info', i18n('noDataProtectionContextsAvailable.label'))
      );
    const createNewDpcButton =
      hasPrivilege(this.#repository, 'modifyContent') ?
        createButton(i18n('dataProtectionContext.newItem'), {
          variant: 'borderless',
          onclick: async () => {
            const newDpcId = await this.#createDPC();
            if (newDpcId) {
              const dataProtectionContexts = await this.#getDataProtectionContexts();
              this.#select.setData(
                dataProtectionContexts.length > 0 ? buildAllSelectOptionGroup(dataProtectionContexts) : [],
              );
              this.#emptyInfo?.remove();
              this.#select.selection = newDpcId.itemName;
              this.#select.classList.toggle('is-invalid', false);
              this.#select.dispatchEvent(new ChangeEvent());
              // update the content
              this.#select.close();
              this.#select.open();
            }
          },
        })
      : undefined;
    const separator = createNewDpcButton !== undefined || this.#emptyInfo !== undefined ? h('hr') : undefined;
    return h(`.${COMPONENT_NAME}--select-dropdown-panel`, [createNewDpcButton, separator, this.#emptyInfo]);
  }

  async #createDPC() {
    const { CreateDataProtectionContextDialog } = await import(
      'cadenza/admin/create-data-protection-context-dialog/create-data-protection-context-dialog'
    );
    return new CreateDataProtectionContextDialog([this.#repository]).connectAndShow({ hidePreviousModals: false });
  }

  get selectedSimpleDataProtectionContext() {
    const selection = this.#select.selection[0];
    const itemDescription = selection.data as ItemDescription;
    if (itemDescription) {
      const dataProtectionContext: SimpleDataProtectionContextDto = {
        itemId: itemDescription.dpcId,
        printName: selection.text,
        description: itemDescription.description,
      };
      return dataProtectionContext;
    }
    return undefined;
  }

  get selectedDataProtectionContextId() {
    return this.selectedSimpleDataProtectionContext?.itemId;
  }
}

function buildAllSelectOptionGroup(
  dataProtectionContexts: SimpleDataProtectionContextDto[],
  dataProtectionContextId?: DataProtectionContextId,
) {
  return dataProtectionContexts.map((dpc) =>
    toResourceSelectOption(
      {
        id: dpc.itemId.itemName,
        text: dpc.printName,
        selected: dataProtectionContextId && repositoryItemIdEquals(dpc.itemId, dataProtectionContextId),
        data: { dpcId: dpc.itemId, description: dpc.description },
      },
      'dataProtectionContext',
    ),
  );
}

function dataProtectionContextTemplateResult(item: SelectOption, searchText: string) {
  return isPlaceholderOption(item) ?
      h<HTMLElement>('div')
    : h<HTMLElement>(`.${COMPONENT_NAME}--select-option-template`, [
        templateResourceResult(item as ResourceSelectItem, searchText),
        (item.data as ItemDescription).description ?
          createInfoIcon((item.data as ItemDescription).description!)
        : undefined,
      ]);
}

function createInfoIcon(toggletip: string) {
  const tooltip = createToggletip({
    toggletip,
    icon: icon(infoIcon),
    size: 'xs',
    toggletipPlacement: 'bottom',
  });
  tooltip.addEventListener('mouseup', (e) => e.stopPropagation());
  return tooltip;
}

customElements.define(COMPONENT_NAME, DataProtectionContextPicker);
